import { StoreInfo } from '../../types';
import { sanitizeStoreCoordinates } from '../../utils/storeUtils';

/**
 * Address -> coordinates for map pins via Mapbox Geocoding v6 (public pk.
 * VITE_MAPBOX_TOKEN), on its free tier of temporary requests. Mapbox's terms
 * forbid storing these results, so nothing here is saved: coordinates and
 * addresses that get saved come from Google (services/googlePlaces.js, through
 * geocodeStore / reverseGeocodeCoords in services/geminiService.ts). Failures
 * resolve to undefined so callers can fall back.
 */
const GEOCODE_TIMEOUT_MS = 4000;
const MAPBOX_GEOCODE_URL = 'https://api.mapbox.com/search/geocode/v6';

// The Bulk Import worker bundles this file for Node, where import.meta.env is {}.
const mapboxToken = (): string | undefined =>
  (import.meta.env?.VITE_MAPBOX_TOKEN as string | undefined)
  || (typeof process !== 'undefined' ? process.env?.VITE_MAPBOX_TOKEN : undefined);

const mapboxForward = async (params: Record<string, string>) => {
  const token = mapboxToken();
  if (!token) return null;
  const query = new URLSearchParams({ ...params, limit: '1', access_token: token });
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), GEOCODE_TIMEOUT_MS);
  try {
    const res = await fetch(`${MAPBOX_GEOCODE_URL}/forward?${query}`, { signal: controller.signal });
    if (!res.ok) return null;
    return (await res.json())?.features?.[0] || null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};

export const geocodeAddress = async (
  query: string,
  near?: { lat: number; lng: number },
): Promise<{ lat: number; lng: number } | undefined> => {
  if (!query.trim()) return undefined;
  const params: Record<string, string> = { q: query };
  if (near) params.proximity = `${near.lng},${near.lat}`;
  const [lng, lat] = (await mapboxForward(params))?.geometry?.coordinates || [];
  return sanitizeStoreCoordinates({ lat, lng });
};

export const enrichStoresWithCoordinates = async (
  stores: StoreInfo[],
  near?: { lat: number; lng: number },
): Promise<StoreInfo[]> =>
  Promise.all(
    stores.map(async (store) => {
      if (store.coordinates || !store.address) return store;
      const coordinates = await geocodeAddress(`${store.name}, ${store.address}`, near);
      return coordinates ? { ...store, coordinates } : store;
    }),
  );
